/**
 * Enhanced API Service
 * Type-safe API methods for hierarchical program operations
 * Integrates with enhanced backend endpoints
 */

import axios, { AxiosInstance } from 'axios';
import {
  EnhancedProgram,
  EnhancedProgramGenerationRequest,
  EnhancedProgramGenerationResponse,
  EnhancedMesocycle,
  EnhancedPhase,
  EnhancedWeek,
  EnhancedWorkout,
  EnhancedTrainingSet,
  LibrarySyncResponse,
  LibraryData,
  DatabaseExercise,
  DatabaseMuscleGroup,
  DatabaseEquipmentCategory,
  DatabaseAdaptation,
  EnhancedGenerationError,
  WeekSchedule,
  ENHANCED_ERROR_CODES
} from '@/types/enhanced';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export class EnhancedApiService {
  private api: AxiosInstance;
  private getAccessToken?: () => Promise<string>;

  constructor(baseURL: string = import.meta.env.VITE_API_URL || 'https://fit-backend-web.fly.dev') {
    this.api = axios.create({
      baseURL,
      timeout: 30000, // 30 seconds for program generation
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // Set the Auth0 token getter function (same as main API service)
  setTokenGetter(getToken: () => Promise<string>) {
    this.getAccessToken = getToken;
  }

  private setupInterceptors() {
    // Request interceptor - use Auth0 token like main API service
    this.api.interceptors.request.use(async (config) => {
      if (this.getAccessToken) {
        try {
          const token = await this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn('Enhanced API: Failed to get access token:', error);
        }
      }
      return config;
    });

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Enhanced API Error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          data: error.response?.data
        });
        return Promise.reject(error);
      }
    );
  }

  // ===== ENHANCED PROGRAM GENERATION =====

  /**
   * Generate enhanced program with hierarchical structure
   */
  async generateEnhancedProgram(
    request: EnhancedProgramGenerationRequest
  ): Promise<EnhancedProgramGenerationResponse> {
    try {
      console.log('🚀 Generating enhanced program:', { 
        clientId: request.clientId,
        muscleGroups: Object.keys(request.muscleGroupPreferences).length,
        equipment: request.availableEquipment?.length || 0
      });

      const response = await this.api.post<EnhancedProgramGenerationResponse>(
        '/api/enhanced/generate-enhanced',
        request
      );

      if (!response.data.success) {
        throw new Error('Enhanced program generation failed');
      }

      console.log('✅ Enhanced program generated successfully:', {
        programId: response.data.data.programId,  // Use programId from data root
        mesocycles: response.data.data.program.mesocycles?.length || 0,
        processingTime: response.data.processingTime
      });

      return response.data;

    } catch (error: any) {
      console.error('❌ Enhanced program generation failed:', error);
      throw this.handleEnhancedError(error, 'PROGRAM_GENERATION_FAILED');
    }
  }

  /**
   * Get enhanced program with complete hierarchical structure
   */
  async getEnhancedProgram(programId: string): Promise<EnhancedProgram> {
    try {
      console.log('📖 Loading enhanced program:', { programId });

      const response = await this.api.get<ApiResponse<{ program: EnhancedProgram }>>(
        `/api/programs/${programId}/enhanced`
      );

      if (!response.data.success || !response.data.data?.program) {
        throw new Error('Enhanced program not found or invalid response');
      }

      console.log('✅ Enhanced program loaded:', {
        programId: response.data.data.program.id,
        mesocycles: response.data.data.program.mesocycles?.length || 0
      });

      return response.data.data.program;

    } catch (error: any) {
      console.error('❌ Failed to load enhanced program:', error);
      throw this.handleEnhancedError(error, 'PROGRAM_LOADING_FAILED');
    }
  }

  // ===== HIERARCHICAL PROGRAM OPERATIONS =====

  /**
   * Update mesocycle
   */
  async updateMesocycle(
    programId: string,
    mesocycleId: string,
    updates: Partial<EnhancedMesocycle>
  ): Promise<EnhancedMesocycle> {
    try {
      const response = await this.api.put<ApiResponse<{ mesocycle: EnhancedMesocycle }>>(
        `/api/programs/${programId}/mesocycles/${mesocycleId}`,
        updates
      );

      if (!response.data.success || !response.data.data?.mesocycle) {
        throw new Error('Failed to update mesocycle');
      }

      return response.data.data.mesocycle;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Update phase
   */
  async updatePhase(
    programId: string,
    phaseId: string,
    updates: Partial<EnhancedPhase>
  ): Promise<EnhancedPhase> {
    try {
      const response = await this.api.put<ApiResponse<{ phase: EnhancedPhase }>>(
        `/api/programs/${programId}/phases/${phaseId}`,
        updates
      );

      if (!response.data.success || !response.data.data?.phase) {
        throw new Error('Failed to update phase');
      }

      return response.data.data.phase;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Update week
   */
  async updateWeek(
    programId: string,
    weekId: string,
    updates: Partial<EnhancedWeek>
  ): Promise<EnhancedWeek> {
    try {
      const response = await this.api.put<ApiResponse<{ week: EnhancedWeek }>>(
        `/api/programs/${programId}/weeks/${weekId}`,
        updates
      );

      if (!response.data.success || !response.data.data?.week) {
        throw new Error('Failed to update week');
      }

      return response.data.data.week;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Update workout
   */
  async updateWorkout(
    programId: string,
    workoutId: string,
    updates: Partial<EnhancedWorkout>
  ): Promise<EnhancedWorkout> {
    try {
      const response = await this.api.put<ApiResponse<{ workout: EnhancedWorkout }>>(
        `/api/programs/${programId}/workouts/${workoutId}`,
        updates
      );

      if (!response.data.success || !response.data.data?.workout) {
        throw new Error('Failed to update workout');
      }

      return response.data.data.workout;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Update individual training set
   */
  async updateTrainingSet(
    workoutId: string,
    setId: string,
    updates: Partial<EnhancedTrainingSet>
  ): Promise<EnhancedTrainingSet> {
    try {
      const response = await this.api.put<ApiResponse<{ set: EnhancedTrainingSet }>>(
        `/api/workouts/${workoutId}/sets/${setId}`,
        updates
      );

      if (!response.data.success || !response.data.data?.set) {
        throw new Error('Failed to update training set');
      }

      return response.data.data.set;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'SET_UPDATE_FAILED');
    }
  }

  // ===== LIBRARY MANAGEMENT =====

  /**
   * Load enhanced exercise library from database
   */
  async loadEnhancedExercises(): Promise<DatabaseExercise[]> {
    try {
      console.log('📚 Loading enhanced exercise library...');

      const response = await this.api.get<ApiResponse<{ exercises: DatabaseExercise[] }>>(
        '/api/enhanced/library/exercises/enhanced'
      );

      if (!response.data.success || !response.data.data?.exercises) {
        throw new Error('Failed to load enhanced exercise library');
      }

      console.log(`✅ Loaded ${response.data.data.exercises.length} enhanced exercises`);
      return response.data.data.exercises;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_LOADING_FAILED');
    }
  }

  /**
   * Load database muscle groups
   */
  async loadDatabaseMuscleGroups(): Promise<DatabaseMuscleGroup[]> {
    try {
      const response = await this.api.get<ApiResponse<{ muscleGroups: DatabaseMuscleGroup[] }>>(
        '/api/enhanced/library/muscle-groups'
      );

      if (!response.data.success || !response.data.data?.muscleGroups) {
        throw new Error('Failed to load database muscle groups');
      }

      console.log('🔍 DEBUG: Muscle groups loaded from enhanced API:', {
        count: response.data.data.muscleGroups.length,
        sampleIds: response.data.data.muscleGroups.slice(0, 3).map(mg => ({ id: mg.id, name: mg.name, idLength: mg.id.length })),
        allIdLengths: response.data.data.muscleGroups.map(mg => mg.id.length)
      });

      return response.data.data.muscleGroups;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_LOADING_FAILED');
    }
  }

  /**
   * Load database equipment categories
   */
  async loadDatabaseEquipmentCategories(): Promise<DatabaseEquipmentCategory[]> {
    try {
      const response = await this.api.get<ApiResponse<{ equipmentCategories: DatabaseEquipmentCategory[] }>>(
        '/api/enhanced/library/equipment-categories'
      );

      if (!response.data.success || !response.data.data?.equipmentCategories) {
        throw new Error('Failed to load database equipment categories');
      }

      return response.data.data.equipmentCategories;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_LOADING_FAILED');
    }
  }

  /**
   * Load database adaptations
   */
  async loadDatabaseAdaptations(): Promise<DatabaseAdaptation[]> {
    try {
      const response = await this.api.get<ApiResponse<{ adaptations: DatabaseAdaptation[] }>>(
        '/api/enhanced/library/adaptations'
      );

      if (!response.data.success || !response.data.data?.adaptations) {
        throw new Error('Failed to load database adaptations');
      }

      return response.data.data.adaptations;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_LOADING_FAILED');
    }
  }

  /**
   * Sync libraries from database
   */
  async syncLibraries(): Promise<LibrarySyncResponse> {
    try {
      console.log('🔄 Syncing libraries from database...');

      const response = await this.api.post<LibrarySyncResponse>(
        '/api/enhanced/library/sync'
      );

      console.log('✅ Libraries synced successfully');
      return response.data;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_SYNC_FAILED');
    }
  }

  /**
   * Load complete library data
   */
  async loadLibraryData(): Promise<LibraryData> {
    try {
      const [exercises, equipmentCategories, muscleGroups, adaptations] = await Promise.all([
        this.loadEnhancedExercises(),
        this.loadDatabaseEquipmentCategories(),
        this.loadDatabaseMuscleGroups(),
        this.loadDatabaseAdaptations()
      ]);

      return {
        exercises,
        equipmentCategories,
        muscleGroups,
        adaptations
      };

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'LIBRARY_LOADING_FAILED');
    }
  }

  // ===== PROGRAM CRUD OPERATIONS (ENHANCED) =====

  /**
   * Get programs with enhanced data support
   */
  async getEnhancedPrograms(params?: {
    page?: number;
    limit?: number;
    coachId?: string;
    clientId?: string;
    status?: string;
    search?: string;
    includeHierarchy?: boolean;
  }): Promise<{
    programs: EnhancedProgram[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    try {
      const query = new URLSearchParams(params as any).toString();
      const response = await this.api.get<ApiResponse<{
        programs: EnhancedProgram[];
        pagination: any;
      }>>(`/api/programs/enhanced?${query}`);

      if (!response.data.success || !response.data.data) {
        throw new Error('Failed to load enhanced programs');
      }

      return response.data.data;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'PROGRAM_LOADING_FAILED');
    }
  }

  /**
   * Create enhanced program
   */
  async createEnhancedProgram(programData: Partial<EnhancedProgram>): Promise<EnhancedProgram> {
    try {
      const response = await this.api.post<ApiResponse<{ program: EnhancedProgram }>>(
        '/api/programs/enhanced',
        programData
      );

      if (!response.data.success || !response.data.data?.program) {
        throw new Error('Failed to create enhanced program');
      }

      return response.data.data.program;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'PROGRAM_CREATION_FAILED');
    }
  }

  /**
   * Update enhanced program
   */
  async updateEnhancedProgram(
    programId: string,
    updates: Partial<EnhancedProgram>
  ): Promise<EnhancedProgram> {
    try {
      const response = await this.api.put<ApiResponse<{ program: EnhancedProgram }>>(
        `/api/programs/${programId}/enhanced`,
        updates
      );

      if (!response.data.success || !response.data.data?.program) {
        throw new Error('Failed to update enhanced program');
      }

      return response.data.data.program;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'PROGRAM_UPDATE_FAILED');
    }
  }

  // ===== HIERARCHICAL OPERATIONS =====

  /**
   * Add mesocycle to program
   */
  async addMesocycle(
    programId: string,
    mesocycleData: Omit<EnhancedMesocycle, 'id' | 'phases'>
  ): Promise<EnhancedMesocycle> {
    try {
      const response = await this.api.post<ApiResponse<{ mesocycle: EnhancedMesocycle }>>(
        `/api/programs/${programId}/mesocycles`,
        mesocycleData
      );

      if (!response.data.success || !response.data.data?.mesocycle) {
        throw new Error('Failed to add mesocycle');
      }

      return response.data.data.mesocycle;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Add phase to mesocycle
   */
  async addPhase(
    programId: string,
    mesocycleId: string,
    phaseData: Omit<EnhancedPhase, 'id' | 'weeks'>
  ): Promise<EnhancedPhase> {
    try {
      const response = await this.api.post<ApiResponse<{ phase: EnhancedPhase }>>(
        `/api/programs/${programId}/mesocycles/${mesocycleId}/phases`,
        phaseData
      );

      if (!response.data.success || !response.data.data?.phase) {
        throw new Error('Failed to add phase');
      }

      return response.data.data.phase;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Add week to phase
   */
  async addWeek(
    programId: string,
    phaseId: string,
    weekData: Omit<EnhancedWeek, 'id' | 'schedule'>
  ): Promise<EnhancedWeek> {
    try {
      const response = await this.api.post<ApiResponse<{ week: EnhancedWeek }>>(
        `/api/programs/${programId}/phases/${phaseId}/weeks`,
        weekData
      );

      if (!response.data.success || !response.data.data?.week) {
        throw new Error('Failed to add week');
      }

      return response.data.data.week;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Add workout to week
   */
  async addWorkout(
    programId: string,
    weekId: string,
    day: keyof WeekSchedule,
    workoutData: Omit<EnhancedWorkout, 'id'>
  ): Promise<EnhancedWorkout> {
    try {
      const response = await this.api.post<ApiResponse<{ workout: EnhancedWorkout }>>(
        `/api/programs/${programId}/weeks/${weekId}/workouts`,
        { ...workoutData, day }
      );

      if (!response.data.success || !response.data.data?.workout) {
        throw new Error('Failed to add workout');
      }

      return response.data.data.workout;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Add training set to workout exercise
   */
  async addTrainingSet(
    workoutId: string,
    exerciseId: string,
    setData: Omit<EnhancedTrainingSet, 'id'>
  ): Promise<EnhancedTrainingSet> {
    try {
      const response = await this.api.post<ApiResponse<{ set: EnhancedTrainingSet }>>(
        `/api/workouts/${workoutId}/exercises/${exerciseId}/sets`,
        setData
      );

      if (!response.data.success || !response.data.data?.set) {
        throw new Error('Failed to add training set');
      }

      return response.data.data.set;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'SET_UPDATE_FAILED');
    }
  }

  // ===== DELETE OPERATIONS =====

  /**
   * Delete mesocycle
   */
  async deleteMesocycle(programId: string, mesocycleId: string): Promise<void> {
    try {
      await this.api.delete(`/api/programs/${programId}/mesocycles/${mesocycleId}`);
    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Delete phase
   */
  async deletePhase(programId: string, phaseId: string): Promise<void> {
    try {
      await this.api.delete(`/api/programs/${programId}/phases/${phaseId}`);
    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Delete week
   */
  async deleteWeek(programId: string, weekId: string): Promise<void> {
    try {
      await this.api.delete(`/api/programs/${programId}/weeks/${weekId}`);
    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Delete workout
   */
  async deleteWorkout(programId: string, workoutId: string): Promise<void> {
    try {
      await this.api.delete(`/api/programs/${programId}/workouts/${workoutId}`);
    } catch (error: any) {
      throw this.handleEnhancedError(error, 'HIERARCHICAL_EDIT_FAILED');
    }
  }

  /**
   * Delete training set
   */
  async deleteTrainingSet(workoutId: string, setId: string): Promise<void> {
    try {
      await this.api.delete(`/api/workouts/${workoutId}/sets/${setId}`);
    } catch (error: any) {
      throw this.handleEnhancedError(error, 'SET_UPDATE_FAILED');
    }
  }

  // ===== VALIDATION AND ANALYSIS =====

  /**
   * Validate enhanced program structure
   */
  async validateProgramStructure(program: EnhancedProgram): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    try {
      const response = await this.api.post<ApiResponse<{
        isValid: boolean;
        errors: string[];
        warnings: string[];
      }>>('/api/programs/validate-structure', { program });

      if (!response.data.success || !response.data.data) {
        throw new Error('Failed to validate program structure');
      }

      return response.data.data;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'VALIDATION_FAILED');
    }
  }

  /**
   * Get program analytics
   */
  async getProgramAnalytics(programId: string): Promise<{
    volumeDistribution: Record<string, number>;
    intensityProgression: Array<{ week: number; intensity: number }>;
    muscleGroupBalance: Record<string, number>;
    progressionAnalysis: {
      phase: string;
      recommendations: string[];
    }[];
  }> {
    try {
      const response = await this.api.get<ApiResponse<any>>(
        `/api/programs/${programId}/analytics`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error('Failed to load program analytics');
      }

      return response.data.data;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'ANALYTICS_LOADING_FAILED');
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Convert legacy program to enhanced program
   */
  async convertToEnhanced(programId: string): Promise<EnhancedProgram> {
    try {
      const response = await this.api.post<ApiResponse<{ program: EnhancedProgram }>>(
        `/api/programs/${programId}/convert-to-enhanced`
      );

      if (!response.data.success || !response.data.data?.program) {
        throw new Error('Failed to convert program to enhanced format');
      }

      return response.data.data.program;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'CONVERSION_FAILED');
    }
  }

  /**
   * Export enhanced program
   */
  async exportEnhancedProgram(
    programId: string,
    format: 'json' | 'pdf' | 'excel'
  ): Promise<Blob> {
    try {
      const response = await this.api.get(
        `/api/programs/${programId}/export/${format}`,
        { responseType: 'blob' }
      );

      return response.data;

    } catch (error: any) {
      throw this.handleEnhancedError(error, 'EXPORT_FAILED');
    }
  }

  // ===== ERROR HANDLING =====

  private handleEnhancedError(error: any, code: keyof typeof ENHANCED_ERROR_CODES): Error {
    const enhancedError: EnhancedGenerationError = {
      code: ENHANCED_ERROR_CODES[code],
      message: error.response?.data?.error || error.message || 'Unknown error occurred',
      severity: this.determineSeverity(error),
      context: {
        details: {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
          timestamp: new Date().toISOString()
        }
      }
    };

    console.error('🚨 Enhanced API Error:', enhancedError);
    return new Error(enhancedError.message);
  }

  private determineSeverity(error: any): 'low' | 'medium' | 'high' {
    const status = error.response?.status;
    
    if (status >= 500) return 'high';      // Server errors
    if (status >= 400) return 'medium';    // Client errors  
    return 'low';                          // Other errors
  }

  // ===== HEALTH CHECK =====

  /**
   * Check enhanced API health
   */
  async healthCheck(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    services: {
      database: boolean;
      pythonWorkers: boolean;
      librarySync: boolean;
    };
    lastLibrarySync?: Date;
  }> {
    try {
      const response = await this.api.get<ApiResponse<any>>('/api/health/enhanced');
      
      if (!response.data.success) {
        throw new Error('Enhanced API health check failed');
      }

      return response.data.data;

    } catch (error: any) {
      return {
        status: 'unhealthy',
        services: {
          database: false,
          pythonWorkers: false,
          librarySync: false
        }
      };
    }
  }
}

// Create singleton instance
export const enhancedApiService = new EnhancedApiService();

// Export for dependency injection in tests
export default EnhancedApiService;
